import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format, subDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear } from 'date-fns';
import { RootState } from '../../store';
import { 
  Colors, 
  Spacing, 
  FontSizes, 
  FontWeights, 
  BorderRadius, 
  Shadows,
  FontStacks,
  Layout
} from '../../constants/theme';
import {
  FilterDimension,
  setFilter,
  setDateRange,
  toggleDrawer,
  selectActiveFilters,
  selectDateRange,
  selectFilteredCount,
  clearAllFilters,
} from '../../store/enhancedFiltersSlice';
import { CalendarIcon, FilterIcon as FilterIconSVG, ChevronDownIcon, CloseIcon } from '../icons/CorporateIcons';

// Modern Glass Morphism Container
const FiltersContainer = styled.div`
  position: sticky;
  top: ${Layout.headerHeightPx};
  z-index: ${Layout.zIndex.content + 15}; /* Lower than dropdowns but higher than page content */
  background: linear-gradient(135deg, 
    ${Colors.surface}95 0%, 
    ${Colors.backgroundDark}90 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${Colors.borderLight}40;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
`;

const FiltersWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.lg}px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${Spacing.md}px;
    padding: ${Spacing.md}px;
  }
`;

const FiltersGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const FilterCard = styled.div`
  position: relative;
  z-index: auto; /* Ensure proper stacking context */
  background: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
    border-color: ${Colors.primary}40;
  }
`;

const FilterButton = styled.button<{ active?: boolean; variant?: 'date' | 'dropdown' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  padding: ${props => props.variant === 'date' ? `${Spacing.md}px ${Spacing.lg}px` : `${Spacing.sm}px ${Spacing.md}px`};
  background: ${props => props.active ? 
    `linear-gradient(135deg, ${Colors.primary}15 0%, ${Colors.secondary}10 100%)` : 
    'transparent'
  };
  border: none;
  border-radius: ${BorderRadius.lg}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.active ? FontWeights.semibold : FontWeights.medium};
  color: ${props => props.active ? Colors.primary : Colors.textPrimary};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  white-space: nowrap;
  min-width: ${props => props.variant === 'date' ? '200px' : '140px'};
  justify-content: space-between;
  
  &:hover {
    background: ${props => props.active ? 
      `linear-gradient(135deg, ${Colors.primary}25 0%, ${Colors.secondary}15 100%)` : 
      Colors.surfaceHover
    };
    color: ${props => props.active ? Colors.primaryDark : Colors.textPrimary};
  }
`;

const FilterIconContainer = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: ${BorderRadius.sm}px;
  background: ${props => props.active ? Colors.secondaryOpacity20 : Colors.backgroundDark};
  color: ${props => props.active ? Colors.primary : Colors.textSecondary};
  transition: all 0.3s ease;
`;

const FilterLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

const FilterTitle = styled.span<{ active?: boolean }>`
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  color: ${props => props.active ? Colors.primary : Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const FilterValue = styled.span<{ active?: boolean }>`
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.active ? FontWeights.semibold : FontWeights.medium};
  color: ${props => props.active ? Colors.primary : Colors.textPrimary};
`;

const ChevronIcon = styled.div<{ isOpen?: boolean; active?: boolean }>`
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
  color: ${props => props.active ? Colors.primary : Colors.textSecondary};
`;

// Enhanced Dropdown Menu
const DropdownMenu = styled.div<{ isOpen: boolean; width?: string }>`
  position: absolute;
  top: calc(100% + ${Spacing.sm}px);
  left: 0;
  width: ${props => props.width || '280px'};
  max-height: 400px;
  background: ${Colors.surface};
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  z-index: 9999; /* Ensure dropdown appears above all other elements */
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  background: linear-gradient(135deg, ${Colors.backgroundDark} 0%, ${Colors.background} 100%);
  border-bottom: 1px solid ${Colors.border};
`;

const DropdownTitle = styled.div`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DropdownBody = styled.div`
  max-height: 320px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${Colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${Colors.border};
    border-radius: ${BorderRadius.round}px;
    
    &:hover {
      background: ${Colors.textTertiary};
    }
  }
`;

const DropdownItem = styled.button<{ selected?: boolean }>`
  width: 100%;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  background: ${props => props.selected ? Colors.secondaryOpacity10 : 'transparent'};
  border: none;
  border-left: 3px solid ${props => props.selected ? Colors.primary : 'transparent'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background: ${props => props.selected ? Colors.secondaryOpacity15 : Colors.surfaceHover};
  }
`;

const ItemLabel = styled.span<{ selected?: boolean }>`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.selected ? FontWeights.semibold : FontWeights.medium};
  color: ${props => props.selected ? Colors.primary : Colors.textPrimary};
`;

const ItemCount = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  background: ${Colors.backgroundDark};
  padding: 2px 8px;
  border-radius: ${BorderRadius.round}px;
`;

// Date Preset Buttons
const DatePresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${Spacing.sm}px;
  padding: ${Spacing.md}px;
`;

const DatePresetButton = styled.button<{ active?: boolean }>`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => props.active ? Colors.primary : Colors.surface};
  color: ${props => props.active ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.md}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
    border-color: ${Colors.primary};
  }
`;

// Action Section
const ActionSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ResultsBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: linear-gradient(135deg, ${Colors.backgroundDark} 0%, ${Colors.background} 100%);
  border: 1px solid ${Colors.borderLight};
  border-radius: ${BorderRadius.lg}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const ResultsCount = styled.span`
  font-weight: ${FontWeights.semibold};
  color: ${Colors.primary};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => {
    switch(props.variant) {
      case 'primary': return `linear-gradient(135deg, ${Colors.primary} 0%, ${Colors.secondary} 100%)`;
      case 'danger': return Colors.danger;
      default: return Colors.surface;
    }
  }};
  color: ${props => {
    switch(props.variant) {
      case 'primary': 
      case 'danger': return Colors.textOnPrimary;
      default: return Colors.textPrimary;
    }
  }};
  border: 1px solid ${props => {
    switch(props.variant) {
      case 'primary': return Colors.primary;
      case 'danger': return Colors.danger;
      default: return Colors.border;
    }
  }};
  border-radius: ${BorderRadius.lg}px;
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${Shadows.md};
    background: ${props => {
      switch(props.variant) {
        case 'primary': return `linear-gradient(135deg, ${Colors.secondary} 0%, ${Colors.primary} 100%)`;
        case 'danger': return Colors.warning;
        default: return Colors.surfaceHover;
      }
    }};
  }
`;

const ActiveFilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${Colors.primary}15;
  border: 1px solid ${Colors.primary}30;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.primary};
`;

const ChipRemoveButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.primary};
  cursor: pointer;
  padding: 0;
  margin-left: ${Spacing.xs}px;
  
  &:hover {
    color: ${Colors.danger};
  }
`;

// Mock data for filters
// Static data with counts - labels will be translated dynamically
const COUNTRY_DATA = [
  { id: 'us', count: 3240 },
  { id: 'uk', count: 892 },
  { id: 'de', count: 654 },
  { id: 'fr', count: 521 },
  { id: 'ca', count: 432 },
  { id: 'au', count: 287 },
  { id: 'jp', count: 198 },
  { id: 'sg', count: 165 },
];

const BUSINESS_UNIT_DATA = [
  { id: 'engineering', count: 1832 },
  { id: 'sales', count: 923 },
  { id: 'marketing', count: 612 },
  { id: 'hr', count: 287 },
  { id: 'finance', count: 394 },
  { id: 'operations', count: 651 },
  { id: 'product', count: 489 },
  { id: 'legal', count: 124 },
];

interface ModernQuickFiltersProps {
  onFiltersChange?: () => void;
}

const ModernQuickFilters: React.FC<ModernQuickFiltersProps> = ({ onFiltersChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeFilters = useSelector(selectActiveFilters);
  const dateRange = useSelector(selectDateRange);
  const filteredCount = useSelector(selectFilteredCount);
  
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && dropdownRefs.current[openDropdown]) {
        const dropdown = dropdownRefs.current[openDropdown];
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleDatePreset = (preset: string) => {
    const now = new Date();
    let start: Date, end: Date;

    switch (preset) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      case 'last7days':
        start = subDays(now, 7);
        end = now;
        break;
      case 'last30days':
        start = subDays(now, 30);
        end = now;
        break;
      case 'thisMonth':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'lastMonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      case 'thisQuarter':
        start = startOfQuarter(now);
        end = endOfQuarter(now);
        break;
      case 'thisYear':
        start = startOfYear(now);
        end = now;
        break;
      default:
        return;
    }

    dispatch(setDateRange({ start, end, preset: preset as any }));
    setOpenDropdown(null);
    if (onFiltersChange) onFiltersChange();
  };

  const handleCountrySelect = (countryId: string) => {
    const currentCountries = activeFilters.country?.values || [];
    const newCountries = currentCountries.includes(countryId)
      ? currentCountries.filter(c => c !== countryId)
      : [...currentCountries, countryId];
    
    dispatch(setFilter({ 
      dimension: 'country' as FilterDimension, 
      values: newCountries 
    }));
    
    if (onFiltersChange) onFiltersChange();
  };

  const handleBusinessUnitSelect = (unitId: string) => {
    const currentUnits = activeFilters.businessUnit?.values || [];
    const newUnits = currentUnits.includes(unitId)
      ? currentUnits.filter(u => u !== unitId)
      : [...currentUnits, unitId];
    
    dispatch(setFilter({ 
      dimension: 'businessUnit' as FilterDimension, 
      values: newUnits 
    }));
    
    if (onFiltersChange) onFiltersChange();
  };

  const handleClearAll = () => {
    dispatch(clearAllFilters());
    if (onFiltersChange) onFiltersChange();
  };

  const handleOpenDrawer = () => {
    dispatch(toggleDrawer());
  };

  // Get translated country options
  const COUNTRY_OPTIONS = COUNTRY_DATA.map(country => ({
    ...country,
    label: t(`filters.countries.${country.id}`)
  }));

  // Get translated business unit options
  const BUSINESS_UNIT_OPTIONS = BUSINESS_UNIT_DATA.map(unit => ({
    ...unit,
    label: t(`filters.units.${unit.id}`)
  }));

  const getCountryLabel = () => {
    const selected = activeFilters.country?.values || [];
    if (selected.length === 0) return t('filters.allCountries');
    if (selected.length === 1) {
      return t(`filters.countries.${selected[0]}`);
    }
    return t('filters.activeFilters', { count: selected.length });
  };

  const getBusinessUnitLabel = () => {
    const selected = activeFilters.businessUnit?.values || [];
    if (selected.length === 0) return t('filters.allBusinessUnits');
    if (selected.length === 1) {
      return t(`filters.units.${selected[0]}`);
    }
    return t('filters.activeFilters', { count: selected.length });
  };

  const formatDateRange = () => {
    if (dateRange.preset === 'lastMonth') {
      return format(dateRange.start, 'MMMM yyyy');
    }
    return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`;
  };

  const activeFilterCount = Object.values(activeFilters).reduce(
    (count, filter) => count + (filter?.values.length || 0), 
    0
  );

  return (
    <FiltersContainer>
      <FiltersWrapper>
        <FiltersGroup>
          {/* Date Range Filter */}
          <FilterCard ref={el => { dropdownRefs.current['date'] = el; }}>
            <FilterButton 
              active={!!dateRange.preset}
              variant="date"
              onClick={() => toggleDropdown('date')}
            >
              <FilterIconContainer active={!!dateRange.preset}>
                <CalendarIcon size={16} />
              </FilterIconContainer>
              <FilterLabel>
                <FilterTitle active={!!dateRange.preset}>{t('filters.dateRange')}</FilterTitle>
                <FilterValue active={!!dateRange.preset}>{formatDateRange()}</FilterValue>
              </FilterLabel>
              <ChevronIcon isOpen={openDropdown === 'date'} active={!!dateRange.preset}>
                <ChevronDownIcon size={12} />
              </ChevronIcon>
            </FilterButton>
            
            <DropdownMenu isOpen={openDropdown === 'date'} width="320px">
              <DropdownHeader>
                <DropdownTitle>{t('filters.selectDateRange')}</DropdownTitle>
              </DropdownHeader>
              <DatePresetGrid>
                <DatePresetButton
                  active={dateRange.preset === 'today'}
                  onClick={() => handleDatePreset('today')}
                >
                  {t('filters.today')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'last7days'}
                  onClick={() => handleDatePreset('last7days')}
                >
                  {t('filters.last7Days')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'last30days'}
                  onClick={() => handleDatePreset('last30days')}
                >
                  {t('filters.last30Days')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'thisMonth'}
                  onClick={() => handleDatePreset('thisMonth')}
                >
                  {t('filters.thisMonth')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'lastMonth'}
                  onClick={() => handleDatePreset('lastMonth')}
                >
                  {t('filters.lastMonth')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'thisQuarter'}
                  onClick={() => handleDatePreset('thisQuarter')}
                >
                  {t('filters.thisQuarter')}
                </DatePresetButton>
                <DatePresetButton
                  active={dateRange.preset === 'thisYear'}
                  onClick={() => handleDatePreset('thisYear')}
                >
                  {t('filters.yearToDate')}
                </DatePresetButton>
              </DatePresetGrid>
            </DropdownMenu>
          </FilterCard>

          {/* Country Filter */}
          <FilterCard ref={el => { dropdownRefs.current['country'] = el; }}>
            <FilterButton 
              active={(activeFilters.country?.values.length || 0) > 0}
              onClick={() => toggleDropdown('country')}
            >
              <FilterLabel>
                <FilterTitle active={(activeFilters.country?.values.length || 0) > 0}>
                  {t('filters.country')}
                </FilterTitle>
                <FilterValue active={(activeFilters.country?.values.length || 0) > 0}>
                  {getCountryLabel()}
                </FilterValue>
              </FilterLabel>
              <ChevronIcon 
                isOpen={openDropdown === 'country'} 
                active={(activeFilters.country?.values.length || 0) > 0}
              >
                <ChevronDownIcon size={12} />
              </ChevronIcon>
            </FilterButton>
            
            <DropdownMenu isOpen={openDropdown === 'country'}>
              <DropdownHeader>
                <DropdownTitle>{t('filters.selectCountries')}</DropdownTitle>
              </DropdownHeader>
              <DropdownBody>
                {COUNTRY_OPTIONS.map(country => (
                  <DropdownItem
                    key={country.id}
                    selected={activeFilters.country?.values.includes(country.id)}
                    onClick={() => handleCountrySelect(country.id)}
                  >
                    <ItemLabel selected={activeFilters.country?.values.includes(country.id)}>
                      {country.label}
                    </ItemLabel>
                    <ItemCount>{country.count.toLocaleString()}</ItemCount>
                  </DropdownItem>
                ))}
              </DropdownBody>
            </DropdownMenu>
          </FilterCard>

          {/* Business Unit Filter */}
          <FilterCard ref={el => { dropdownRefs.current['businessUnit'] = el; }}>
            <FilterButton 
              active={(activeFilters.businessUnit?.values.length || 0) > 0}
              onClick={() => toggleDropdown('businessUnit')}
            >
              <FilterLabel>
                <FilterTitle active={(activeFilters.businessUnit?.values.length || 0) > 0}>
                  {t('filters.businessUnit')}
                </FilterTitle>
                <FilterValue active={(activeFilters.businessUnit?.values.length || 0) > 0}>
                  {getBusinessUnitLabel()}
                </FilterValue>
              </FilterLabel>
              <ChevronIcon 
                isOpen={openDropdown === 'businessUnit'} 
                active={(activeFilters.businessUnit?.values.length || 0) > 0}
              >
                <ChevronDownIcon size={12} />
              </ChevronIcon>
            </FilterButton>
            
            <DropdownMenu isOpen={openDropdown === 'businessUnit'}>
              <DropdownHeader>
                <DropdownTitle>{t('filters.selectBusinessUnits')}</DropdownTitle>
              </DropdownHeader>
              <DropdownBody>
                {BUSINESS_UNIT_OPTIONS.map(unit => (
                  <DropdownItem
                    key={unit.id}
                    selected={activeFilters.businessUnit?.values.includes(unit.id)}
                    onClick={() => handleBusinessUnitSelect(unit.id)}
                  >
                    <ItemLabel selected={activeFilters.businessUnit?.values.includes(unit.id)}>
                      {unit.label}
                    </ItemLabel>
                    <ItemCount>{unit.count.toLocaleString()}</ItemCount>
                  </DropdownItem>
                ))}
              </DropdownBody>
            </DropdownMenu>
          </FilterCard>
        </FiltersGroup>

        <ActionSection>
          {/* Results Badge */}
          {filteredCount > 0 && (
            <ResultsBadge>
              <ResultsCount>{filteredCount.toLocaleString()}</ResultsCount>
              results
            </ResultsBadge>
          )}

          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <ActiveFilterChip>
              {activeFilterCount} active
              <ChipRemoveButton onClick={handleClearAll}>
                <CloseIcon size={12} />
              </ChipRemoveButton>
            </ActiveFilterChip>
          )}

          {/* More Filters Button */}
          <ActionButton variant="primary" onClick={handleOpenDrawer}>
            <FilterIconSVG size={16} />
            More Filters
          </ActionButton>
        </ActionSection>
      </FiltersWrapper>
    </FiltersContainer>
  );
};

export default ModernQuickFilters;